import React, { useCallback } from "react";
import { formatPhoneNumber } from "react-phone-number-input";
import { useDispatch } from "react-redux";

import {
	ACHProcessorData as ach,
	AddressCoordinatesConstants,
	CreditReviewFields,
	DaysToDeliveryFieldsNames,
	DesiredLimitsFields,
	NextReviewDateFields,
} from "globalConstants";
import { getFormattedDate } from "helpers";
import {
	FeeProgramByConstants,
	TransactionFeeTypeByValue,
} from "helpers/enums-helper";
import { uiActions } from "store";
import { FundingTypes, TypeOfBusinessValues, UWDecisions } from "types";
import { float6Normalizer, floatWithComma2Normalizer } from "validators";

import DisableLocationIcon from "static/images/disableLocation.png";
import LocationIcon from "static/images/location.png";
import styles from "./merchantInformation.module.scss";

export const getUnderwritingItems = data => {
	const underwritingApprovedAt = getFormattedDate({
		date: data?.underwritingApprovedAt,
		format: "MM/DD/YYYY",
	});
	const UWDecision = UWDecisions.getByName(
		data?.underwritingDecision,
	)?.displayName;

	return [
		{
			row: [
				{
					label: "Underwriting Decision",
					value: UWDecision,
				},
				{ label: "Approval Date", value: underwritingApprovedAt },
			],
		},
		{
			row: [
				{
					label: "Avg Monthly Profit",
					value: floatWithComma2Normalizer(
						data?.avgMonthlyProfit,
						"$",
					),
				},
			],
		},
	];
};

export const getDbaItems = data => {
	const acceptedCardPreviously = `${
		data?.acceptedCardPreviously ? "Yes" : "No"
	} `;

	const businessEstablishedDate = getFormattedDate({
		date: data?.businessEstablishedDate,
		format: "MM/DD/YYYY",
	});

	const cardAcceptanceMethod = `
	Swiped: ${data?.swipedCardAcceptance || 0}%,
	MO / TO: ${data?.keyedCardAcceptance || 0}%;
	eCommerce: ${data?.ecommerceCardAcceptance || 0}%`;

	const typeOfBusinessIs = (
		<>
			<span>MCC-{data?.mccCode}</span>
			<br />
			<span>{data?.mccCodeDescription}</span>
		</>
	);

	const typeOfBusiness = TypeOfBusinessValues.getDisplayName(
		data?.typeOfBusiness,
	);

	const getAddress = address => {
		const addressLine1 = address?.address_line1 || "";
		const addressLine2 =
			address?.address_line2 !== null ? address?.address_line2 : "";
		const addressLine2Comma = addressLine2 ? `, ${addressLine2}` : "";
		const city = address?.city || "";
		const country = address?.country_code || "";
		const postalCode = address?.postal_code || "";

		return (
			<p>
				{`${addressLine1}${
					addressLine1 && ","
				}${addressLine2Comma} ${city} ${country} ${postalCode}`}
			</p>
		);
	};

	const GeoLocationMap = ({ addressTitle, address, addressLocation }) => {
		const dispatch = useDispatch();

		type AddressLocation = {
			lat: number;
			lng: number;
		};

		const isAddressExisting =
			addressLocation?.latitude && addressLocation?.longitude;

		const isManualAddress =
			AddressCoordinatesConstants.ManualAddress.lat ===
				addressLocation?.latitude ||
			AddressCoordinatesConstants.ManualAddress.lng ===
				addressLocation?.longitude;

		const showGeoLocationMap = useCallback(
			(locationTitle, currentAddress, addressLocation) => {
				let initialLocation: AddressLocation | null = null;
				if (isAddressExisting) {
					initialLocation = {
						lat: addressLocation.latitude,
						lng: addressLocation.longitude,
					};
				} else {
					initialLocation = { lat: 0, lng: 0 };
				}

				dispatch(
					uiActions.showModal("GEO_LOCATION", {
						title: locationTitle,
						initialLocation,
						currentAddress,
					}),
				);
			},
			[dispatch, isAddressExisting],
		);

		if (!isAddressExisting) {
			return null;
		}

		return isManualAddress ? (
			<img
				src={DisableLocationIcon}
				alt=""
				className={styles.locationIcon}
			/>
		) : (
			<img
				src={LocationIcon}
				alt=""
				className={styles.locationIcon}
				onClick={() =>
					showGeoLocationMap?.(addressTitle, address, addressLocation)
				}
			/>
		);
	};

	return [
		{
			row: [
				{
					label: "Legal Business Name",
					value: data?.legalName,
				},
				{
					label: "Business Est Date",
					value: businessEstablishedDate,
				},
			],
		},
		{
			row: [
				{
					label: "DBA Business Address",
					value: (
						<div className="d-flex">
							{getAddress(data?.dbaBusinessAddress)}
							<GeoLocationMap
								addressTitle="DBA Address"
								address={
									data?.dbaBusinessAddress?.address_line1
								}
								addressLocation={data?.dbaBusinessAddress}
							/>
						</div>
					),
				},
				{
					label: "Card Acceptance Method",
					value: cardAcceptanceMethod,
				},
			],
		},
		{
			row: [
				{
					label: "Legal Address",
					value: (
						<div className="d-flex ">
							{getAddress(data?.legalAddress)}
							<GeoLocationMap
								addressTitle="Legal Address"
								address={
									data?.dbaBusinessAddress?.address_line1
								}
								addressLocation={data?.legalAddress}
							/>
						</div>
					),
				},
				{
					label: "Accepted Card Previously?",
					value: acceptedCardPreviously,
				},
			],
		},
		{
			row: [
				{
					label: "Business Phone",
					value: formatPhoneNumber(data?.businessPhone),
				},
				{
					label: "Previous Processor",
					value: data?.previousProcessor,
				},
			],
		},
		{
			row: [
				{
					label: "What Type of Business is This?",
					value: typeOfBusinessIs,
				},
				{
					label: "Website",
					value: data?.website,
				},
			],
		},
		{
			row: [
				{
					label: "Detailed Explanation of Product(s) or Service(s) Sold",
					value: data?.detailedExplanationWhatDoYouSell,
				},
				{
					label: "Processor",
					value: data?.processor?.name,
				},
			],
		},
		{
			row: [
				{
					label: "Type of Business",
					value: typeOfBusiness,
				},
				{
					label: "Sponsor",
					value: data?.sponsor?.name,
				},
			],
		},
		{
			row: [
				{ label: "Federal Tax ID", value: data?.federalTaxID },
				{
					label: "Credit Exposure",
					value: data?.creditExposure
						? floatWithComma2Normalizer(data?.creditExposure, "$")
						: "",
				},
			],
		},
	];
};

export const getOwnersItems = data => {
	const getUnderwritingTab = underwritingDecision =>
		underwritingDecision === UWDecisions.Approved.name
			? "historical"
			: "concurrence-conditions";

	return [
		{
			id: data?.owner1?.ownerId,
			firstName: data?.owner1?.firstName,
			lastName: data?.owner1?.lastName,
			applicationId: data?.owner1?.applicationId,
			uwTab: getUnderwritingTab(data?.owner1?.underwritingDecision),
		},
		{
			id: data?.owner2?.ownerId,
			firstName: data?.owner2?.firstName,
			lastName: data?.owner2?.lastName,
			applicationId: data?.owner2?.applicationId,
			uwTab: getUnderwritingTab(data?.owner2?.underwritingDecision),
		},
		{
			id: data?.owner3?.ownerId,
			firstName: data?.owner3?.firstName,
			lastName: data?.owner3?.lastName,
			applicationId: data?.owner3?.applicationId,
			uwTab: getUnderwritingTab(data?.owner3?.underwritingDecision),
		},
		{
			id: data?.owner4?.ownerId,
			firstName: data?.owner4?.firstName,
			lastName: data?.owner4?.lastName,
			applicationId: data?.owner4?.applicationId,
			uwTab: getUnderwritingTab(data?.owner4?.underwritingDecision),
		},
	];
};

// this function is also used in the getExecutiveAgentItems
export const getFeesItems = fees => {
	const getFeeRow = (label, value) => ({
		row: [
			{
				label,
				value: value?.toString(),
			},
		],
	});

	if (fees?.feeProgram === FeeProgramByConstants.INTERCHANGE_PLUS) {
		return [
			getFeeRow(
				"Fee Program",
				TransactionFeeTypeByValue(fees?.feeProgram),
			),
			getFeeRow(
				"Interchange+ V/MC/D, %",
				float6Normalizer(fees?.interchangePlusVmcdFee),
			),
			getFeeRow(
				"Interchange+ Offline Debit, %",
				float6Normalizer(fees?.interchangePlusOfflineDebitFee),
			),
			getFeeRow(
				"Interchange+ Amex, %",
				float6Normalizer(fees?.interchangePlusAmexFee),
			),
		];
	} else if (fees?.feeProgram === FeeProgramByConstants.FLAT_RATE) {
		return [
			getFeeRow(
				"Fee Program",
				TransactionFeeTypeByValue(fees?.feeProgram),
			),
			getFeeRow("Flat Rate, %", float6Normalizer(fees?.flatRateFee)),
		];
	} else if (fees?.feeProgram === FeeProgramByConstants.THREE_TIER) {
		return [
			getFeeRow(
				"Fee Program",
				TransactionFeeTypeByValue(fees?.feeProgram),
			),
			getFeeRow(
				"Qual V/MC/D, %",
				float6Normalizer(fees?.threeTierQualVmcdFee),
			),
			getFeeRow(
				"Qual Amex, %",
				float6Normalizer(fees?.threeTierQualAmexFee),
			),
			getFeeRow(
				"Qual Offline Debit, %",
				float6Normalizer(fees?.threeTierQualOfflineDebitFee),
			),
			getFeeRow(
				"Non-Qual V/MC/D, %",
				float6Normalizer(fees?.threeTierNonQualVmcdFee),
			),
			getFeeRow(
				"Non-Qual Amex, %",
				float6Normalizer(fees?.threeTierNonQualAmexFee),
			),
			getFeeRow(
				"Non-Qual Offline Debit, %",
				float6Normalizer(fees?.threeTierNonQualOfflineDebitFee),
			),
			getFeeRow(
				"Mid-Qual V/MC/D, %",
				float6Normalizer(fees?.threeTierMidQualVmcdFee),
			),
			getFeeRow(
				"Mid-Qual Amex, %",
				float6Normalizer(fees?.threeTierMidQualAmexFee),
			),
			getFeeRow(
				"Mid-Qual Offline Debit, %",
				float6Normalizer(fees?.threeTierMidQualOfflineDebitFee),
			),
		];
	} else if (fees?.feeProgram === FeeProgramByConstants.ERR) {
		return [
			getFeeRow(
				"Fee Program",
				TransactionFeeTypeByValue(fees?.feeProgram),
			),
			getFeeRow("Qual V/MC/D, %", float6Normalizer(fees?.errQualVmcdFee)),
			getFeeRow("Qual Amex, %", float6Normalizer(fees?.errQualAmexFee)),
			getFeeRow(
				"Qual Offline Debit, %",
				float6Normalizer(fees?.errQualOfflineDebitFee),
			),
			getFeeRow(
				"Non-Qual V/MC/D, %",
				float6Normalizer(fees?.errNonQualVmcdFee),
			),
			getFeeRow(
				"Non-Qual Amex, %",
				float6Normalizer(fees?.errNonQualAmexFee),
			),
			getFeeRow(
				"Non-Qual Offline Debit, %",
				float6Normalizer(fees?.errNonQualOfflineDebitFee),
			),
		];
	} else if (fees?.feeProgram === FeeProgramByConstants.DUAL_FLAT_RATE) {
		return [
			getFeeRow(
				"Fee Program",
				TransactionFeeTypeByValue(fees?.feeProgram),
			),
			getFeeRow(
				"Card Present Rate, %",
				float6Normalizer(fees?.dualFlatRateCardPresentFee),
			),
			getFeeRow(
				"Card Present Authorization Fee, $",
				float6Normalizer(fees?.dualFlatRateCardPresentAuthFee),
			),
			getFeeRow(
				"Card Not Present Rate, %",
				float6Normalizer(fees?.dualFlatRateCardNotPresentFee),
			),
			getFeeRow(
				"Card Not Present Authorization Fee, $",
				float6Normalizer(fees?.dualFlatRateCardNotPresentAuthFee),
			),
		];
	} else {
		return [];
	}
};

export const getDesiredLimitsItems = data => {
	const fundingType = FundingTypes.getDisplayName(data?.fundingType);
	const dailyDiscount = data?.isDailyDiscount ? (
		<>&#10004; Daily Discount</>
	) : (
		""
	);

	const fundingTypesValue = (
		<>
			<div>
				<span className="pr-2">{fundingType}</span>
				<span className="pl-2">{dailyDiscount}</span>
			</div>
		</>
	);
	return [
		{
			row: [{}, { label: "V / MC / D" }, { label: "Amex" }],
		},
		{
			row: [
				{ label: "AMV" },
				{
					value:
						floatWithComma2Normalizer(data?.vmcdMonthly, "$") || "",
				},
				{
					value:
						floatWithComma2Normalizer(data?.amexMonthly, "$") || "",
				},
			],
		},
		{
			row: [
				{ label: "AVT" },
				{
					value:
						floatWithComma2Normalizer(data?.vmcdAvgTicket, "$") ||
						"",
				},
				{
					value:
						floatWithComma2Normalizer(data?.amexAvgTicket, "$") ||
						"",
				},
			],
		},
		{
			row: [
				{ label: "HT" },
				{
					value:
						floatWithComma2Normalizer(data?.vmcdHighTicket, "$") ||
						"",
				},
				{
					value:
						floatWithComma2Normalizer(data?.amexHighTicket, "$") ||
						"",
				},
			],
		},
		{
			row: [
				{ label: "Funding Types" },
				{
					value: fundingTypesValue,
				},
			],
		},
	];
};

export const mapRiskFromData = (data: { [key: string]: any }) => {
	const mappedData = {
		[NextReviewDateFields.NextChargebackReviewDate.name]:
			data[NextReviewDateFields.NextChargebackReviewDate.beName],
		[NextReviewDateFields.NextCreditReviewDate.name]:
			data[NextReviewDateFields.NextCreditReviewDate.beName],

		[DesiredLimitsFields.AvgTicketAMEX.name]: floatWithComma2Normalizer(
			data[DesiredLimitsFields.AvgTicketAMEX.beName],
		),
		[DesiredLimitsFields.AvgTicketVMCD.name]: floatWithComma2Normalizer(
			data[DesiredLimitsFields.AvgTicketVMCD.beName],
		),
		[DesiredLimitsFields.DailyDiscount.name]:
			data[DesiredLimitsFields.DailyDiscount.beName],

		[DesiredLimitsFields.FundingTypes.name]: FundingTypes.getByValue(
			data[DesiredLimitsFields.FundingTypes.beName],
		),
		[DesiredLimitsFields.HighTicketAMEX.name]: floatWithComma2Normalizer(
			data[DesiredLimitsFields.HighTicketAMEX.beName],
		),
		[DesiredLimitsFields.HighTicketVMCD.name]: floatWithComma2Normalizer(
			data[DesiredLimitsFields.HighTicketVMCD.beName],
		),
		[DesiredLimitsFields.MonthlyAMEX.name]: floatWithComma2Normalizer(
			data[DesiredLimitsFields.MonthlyAMEX.beName],
		),
		[DesiredLimitsFields.MonthlyVMCD.name]: floatWithComma2Normalizer(
			data[DesiredLimitsFields.MonthlyVMCD.beName],
		),

		[DaysToDeliveryFieldsNames.SameDay.name]: floatWithComma2Normalizer(
			data[DaysToDeliveryFieldsNames.SameDay.beName],
		),
		[DaysToDeliveryFieldsNames.OneThreeDays.name]:
			floatWithComma2Normalizer(
				data[DaysToDeliveryFieldsNames.OneThreeDays.beName],
			),
		[DaysToDeliveryFieldsNames.FourSevenDays.name]:
			floatWithComma2Normalizer(
				data[DaysToDeliveryFieldsNames.FourSevenDays.beName],
			),
		[DaysToDeliveryFieldsNames.EightFourteenDays.name]:
			floatWithComma2Normalizer(
				data[DaysToDeliveryFieldsNames.EightFourteenDays.beName],
			),
		[DaysToDeliveryFieldsNames.FifteenThirtyDays.name]:
			floatWithComma2Normalizer(
				data[DaysToDeliveryFieldsNames.FifteenThirtyDays.beName],
			),
		[DaysToDeliveryFieldsNames.ThirtyPlusDays.name]:
			floatWithComma2Normalizer(
				data[DaysToDeliveryFieldsNames.ThirtyPlusDays.beName],
			),
		[DaysToDeliveryFieldsNames.DaysOverThirty.name]:
			data[DaysToDeliveryFieldsNames.DaysOverThirty.beName],

		[CreditReviewFields.ChargebackVolume.name]: floatWithComma2Normalizer(
			data[CreditReviewFields.ChargebackVolume.beName],
		),
		[CreditReviewFields.ReserveAmount.name]: floatWithComma2Normalizer(
			data[CreditReviewFields.ReserveAmount.beName],
		),
		[CreditReviewFields.Reserve.name]: floatWithComma2Normalizer(
			data[CreditReviewFields.Reserve.beName],
		),
		[CreditReviewFields.RiskExposure.name]: floatWithComma2Normalizer(
			data[CreditReviewFields.RiskExposure.beName],
		),
	};

	return mappedData;
};

export const mapAchProcessorFromServer = (data: { [key: string]: any }) => {
	const mappedData = {
		// StraddleIdentifier
		[ach.StraddleIdentifier.name]: data?.[ach.StraddleIdentifier.beName],
		// StatementSection
		[ach.StatementDescriptor.name]: data?.[ach.StatementDescriptor.beName],
		[ach.UseOfPaymentServiceDescription.name]:
			data?.[ach.UseOfPaymentServiceDescription.beName],
		// WhoAreYourCustomers
		[ach.CustomerType.name]:
			data?.[ach.CustomerType.beName] ||
			ach.CustomerType.Individuals.value,
		// HowReceivePaymentAuth
		[ach.PaymentAuthorizationType.name]:
			data?.[ach.PaymentAuthorizationType.beName] ||
			ach.PaymentAuthorizationType.SignedContract.value,
		// AchDesiredLimits
		[ach.MonthlyVolumeAch.name]: data?.[ach.MonthlyVolumeAch.beName],
		[ach.MonthlyVolumeAchPayouts.name]:
			data?.[ach.MonthlyVolumeAchPayouts.beName],
		[ach.HighTicketAch.name]: data?.[ach.HighTicketAch.beName],
		[ach.HighTicketAchPayouts.name]:
			data?.[ach.HighTicketAchPayouts.beName],
		[ach.TicketCountAch.name]: data?.[ach.TicketCountAch.beName],
		[ach.TicketCountAchPayouts.name]:
			data?.[ach.TicketCountAchPayouts.beName],
		// AchBankInformation
		[ach.IsApplyBankAccountData.name]:
			data?.[ach.IsApplyBankAccountData.beName].toString(),
		[ach.BillingRouting.name]: data?.[ach.BillingRouting.beName],
		[ach.BillingBank.name]: data?.[ach.BillingBank.beName],
		[ach.BillingAccount.name]: data?.[ach.BillingAccount.beName],
		[ach.BillingAccountName.name]: data?.[ach.BillingAccountName.beName],
		[ach.CustomerType.name]:
			data?.[ach.CustomerType.beName] ||
			ach.CustomerType.Individuals.value,
		[ach.PaymentAuthorizationType.name]:
			data?.[ach.PaymentAuthorizationType.beName] ||
			ach.PaymentAuthorizationType.SignedContract.value,
	};

	return mappedData;
};

export const mapAchProcessorToServer = (
	values: { [key: string]: any },
	merchantGeneralData: { [key: string]: any },
) => {
	const copyBillingInfo =
		values?.[ach.IsApplyBankAccountData.name] === "true";

	const mappedData = {
		// StraddleIdentifier
		[ach.StraddleIdentifier.beName]:
			values?.[ach.StraddleIdentifier.name] || null,
		// StatementSection
		[ach.StatementDescriptor.beName]:
			values?.[ach.StatementDescriptor.name],
		[ach.UseOfPaymentServiceDescription.beName]:
			values?.[ach.UseOfPaymentServiceDescription.name],
		// WhoAreYourCustomers
		[ach.CustomerType.beName]: values?.[ach.CustomerType.name],
		// HowReceivePaymentAuth
		[ach.PaymentAuthorizationType.beName]:
			values?.[ach.PaymentAuthorizationType.name],
		// AchDesiredLimits
		[ach.MonthlyVolumeAch.beName]: values?.[ach.MonthlyVolumeAch.name],
		[ach.MonthlyVolumeAchPayouts.beName]:
			values?.[ach.MonthlyVolumeAchPayouts.name],
		[ach.HighTicketAch.beName]: values?.[ach.HighTicketAch.name],
		[ach.HighTicketAchPayouts.beName]:
			values?.[ach.HighTicketAchPayouts.name],
		[ach.TicketCountAch.beName]: values?.[ach.TicketCountAch.name],
		[ach.TicketCountAchPayouts.beName]:
			values?.[ach.TicketCountAchPayouts.name],
		// AchBankInformation
		[ach.IsApplyBankAccountData.beName]:
			values?.[ach.IsApplyBankAccountData.name],
		[ach.BillingRouting.beName]: copyBillingInfo
			? merchantGeneralData?.routing
			: values?.[ach.BillingRouting.name],
		[ach.BillingBank.beName]: copyBillingInfo
			? merchantGeneralData?.bank
			: values?.[ach.BillingBank.name],
		[ach.BillingAccount.beName]: copyBillingInfo
			? merchantGeneralData?.account
			: values?.[ach.BillingAccount.name],
		[ach.BillingAccountName.beName]: copyBillingInfo
			? merchantGeneralData?.accountName
			: values?.[ach.BillingAccountName.name],
	};

	return mappedData;
};
